// ============================================================
// HOUSE OF CARDS — puzzleEngine v3 — BRANCHING CHAIN
// Pure logic, no React. Every interaction returns a "result":
//   { ok, message, grantItems?, consumeItems?, setFlags?, trophy?,
//     unlockDoor?, majorTask?, startScript?, addDrive? }
// App.js applies results to state. Add new puzzles by adding
// entries to USE_RULES / EASTER_EGGS — no engine changes needed.
//
// v3 additions (all backward compatible):
//   addDrive: {mercy:2}  — any result may nudge the ending drives
//                          (see endingsEngine.js). Used by the new
//                          alternate solutions, hidden chambers, and
//                          mercy/cruelty interactions that shape the
//                          ending without blocking progression.
//   Alternate solutions  — several gates now accept MORE THAN ONE
//                          item. Which one you use seeds a drive, so
//                          two players can both progress and still
//                          earn different endings.
//   Hidden chambers      — fed_the_veins (Cellar) and found_the_way_out
//                          (Master wardrobe) set the flags that unlock
//                          the Houseplant and The Uninsured endings.
// NOTE: never name exports "useXxx" — ESLint treats them as React Hooks.
// ============================================================
import { ITEMS, DOORS } from "./roomData";

// ── ITEM-ON-HOTSPOT RULES ─────────────────────────────────────
// key: `${itemId}>${hotspotId}`
// Optional per-rule fields:
//   requiresFlags — all must be true, else failMessage
//   once          — flag; if already true, repeatMessage instead
export const USE_RULES = {
  // Crescent key winds the music box → Rust Key (Kitchen)
  // AND this is the moment Tome's sight opens. The lullaby doesn't just
  // free the key — it pries something loose behind his eyes. From here
  // the house can be seen as it truly is: Unstable Eyes are unlocked.
  // (The room itself doesn't re-skin yet — only objects appear/vanish —
  //  but shadow-things that were always here become visible now.)
  "crescent_key>music_box": {
    result: {
      ok: true,
      message:
        "The crescent key slides into the moon-shaped hole. You wind it. A lullaby you almost remember plays itself out — and the box yawns open. Inside: a RUST KEY.\n\nThe last note doesn't stop when the box does. It keeps going, thin and high, somewhere behind your eyes — and for one sick, swimming second the room is WRONG, wetter, older, breathing. Then it's just the nursery again.\n\nBut you can feel it now. The way you'd feel a draft from a door you can't see. If you let yourself look — really look — you think the house would show you.",
      consumeItems: ["crescent_key"],
      grantItems: ["rust_key"],
      setFlags: { music_box_opened: true, unstableEyes: true },
      majorTask: true,
      trophy: { id: "unstable_eyes", name: "Unstable Eyes", desc: "The lullaby opened something. Now you see what the house keeps in the corners of itself." },
    },
  },

  // Tinder box lights the stove
  "tinder_box>stove": {
    once: "stove_lit",
    repeatMessage: "The burners are already going, low and blue and patient.",
    result: {
      ok: true,
      message:
        "Flint, steel, one strike — the burners catch like they were holding their breath for it. Six rings of low blue flame. The empty pot, absurdly, begins to smell BETTER.\n\nNow then. Anything around here need melting?",
      setFlags: { stove_lit: true },
    },
  },

  // Ice block on the LIT stove → Brass Valve (Bathroom door)
  "ice_block>stove": {
    requiresFlags: ["stove_lit"],
    failMessage:
      "You set the ice on a dead burner. The ice wins. It might even be getting COLDER. The stove needs fire first.",
    result: {
      ok: true,
      message:
        "You set the block on the flames. The ice doesn't melt so much as SURRENDER — a long hiss, a puddle that crawls away under the icebox on its own, and there, hot to the touch:\n\nA BRASS VALVE-WHEEL.",
      consumeItems: ["ice_block"],
      grantItems: ["brass_valve"],
      setFlags: { ice_melted: true },
      majorTask: true,
    },
  },

  // Iron crank on the dumbwaiter → Tarnished Key (Master door)
  "iron_crank>dumbwaiter": {
    result: {
      ok: true,
      message:
        "The crank seats onto the spindle with a click of pure relief. You haul. Rope groans. Somewhere below, the patient little bell rings ONCE, satisfied —\n\n— and the car arrives: a silver tray, a doily, and on the doily, presented like room service, a TARNISHED KEY.",
      consumeItems: ["iron_crank"],
      grantItems: ["tarnished_key"],
      setFlags: { dumbwaiter_used: true },
      majorTask: true,
    },
  },

  // Tinder box lights the master fireplace → reveals the scorched warning
  "tinder_box>fireplace": {
    once: "fireplace_lit",
    repeatMessage: "The fire is going strong. It looks grateful. Fires shouldn't look grateful.",
    result: {
      ok: true,
      message:
        "One strike. The fifty-year-old kindling takes instantly — and the room changes. Shadows lean. Warmth crawls up the wall...\n\n...and above the mantle, in the raking light, WORDS begin bleeding through the wallpaper.",
      setFlags: { fireplace_lit: true },
    },
  },

  // THE TOLL — feed the Mouth Door the painted rock → finale router
  "painted_rock>mouth_door_open": {
    requiresFlags: ["all_doors_open"],
    failMessage: "The mouth ignores the offering. Teeth first.",
    result: {
      ok: true,
      consumeItems: ["painted_rock"],
      setFlags: { toll_paid: true },
      majorTask: true,
      startScript: "finale",
    },
  },

  // ── ALTERNATE TOLL — feed it the LOCKET instead (mercy route) ──
  // You can satisfy the mouth with the thing you love most rather than
  // the thing you valued least. Big mercy seed; routes toward reunion.
  "tarnished_locket>mouth_door_open": {
    requiresFlags: ["all_doors_open"],
    failMessage: "The mouth waits. Open all six teeth first.",
    result: {
      ok: true,
      message:
        "You don't give it the worthless thing. You give it the only thing you'd never throw away.\n\nThe mouth goes very still. Then, impossibly gentle, it takes the locket the way you'd take a sleeping child from someone's arms.",
      consumeItems: ["tarnished_locket"],
      setFlags: { toll_paid: true, gave_what_mattered: true },
      addDrive: { mercy: 3 },
      majorTask: true,
      startScript: "finale",
    },
  },

  // ── FEED THE VEINS — Cellar root cluster (grotesque Bloom seed) ──
  // Uses the TORN PAGE (a clue item that is never consumed and isn't a
  // progression key), so feeding the veins can never softlock the main
  // chain. You read the lullaby to the roots the way you'd read to a child.
  "torn_page>root_veins": {
    once: "fed_the_veins",
    repeatMessage: "The roots are sated. They pulse slower now, content, coiled toward the warmth of your voice.",
    result: {
      ok: true,
      message:
        "You don't know why you do it. You kneel by the bundle of roots and you read the torn page aloud to them — the moons, dark to light — the way you'd settle a child.\n\nThey DRINK the sound. Everywhere at once the veins flush a healthier red, coiling toward you. The whole house sighs the way a dry plant sighs after rain.\n\nYou keep the page. You have a bad feeling you've just made a friend that grows.",
      setFlags: { fed_the_veins: true },
      addDrive: { mercy: 2, curiosity: 1 },
      trophy: { id: "green_thumb", name: "Green Thumb", desc: "You read the house a bedtime story. It will remember being read to." },
    },
  },

  // ── THE BUILDER'S DOOR — behind the Master wardrobe (escape seed) ──
  // Reusable item: the tinder box (never consumed). You burn the coats
  // away to reach the false back. But the escape is mainly surfaced as
  // an in-world CHOICE on the wardrobe too (see CHOICE_OUTCOMES).
  "tinder_box>wardrobe": {
    once: "found_the_way_out",
    repeatMessage: "The builder's door stands behind the burned-back coats. It wasn't there before you remembered it.",
    result: {
      ok: true,
      message:
        "You touch the candle stub to the hem of the breathing coats. They go up fast and quiet, and behind their ash is a small plain door — the kind a builder cuts for himself. The kind YOU cut, and made yourself forget, because some part of you always knew you'd need a way out.\n\nIt's locked from this side. You'll decide whether to open it.",
      setFlags: { found_the_way_out: true },
      addDrive: { defiance: 3, curiosity: 1 },
      trophy: { id: "measure_twice", name: "Measure Twice", desc: "You built a way out and forgot on purpose. You found it anyway." },
    },
  },

  // ── A MERCY: cover the warm bed (master) — surrender/mercy seed ──
  "tarnished_locket>bed": {
    once: "tended_the_bed",
    repeatMessage: "The bed is tucked. Whatever's in it is resting easier than you are.",
    result: {
      ok: true,
      message:
        "You set the locket on the dented pillow and draw the blanket up over the warm, empty hollow, the way you'd tuck someone in.\n\nThe warmth doesn't fade. But it stops feeling like a threat and starts feeling like a sleeping breath. Small mercy. The house notices every one.",
      setFlags: { tended_the_bed: true },
      addDrive: { mercy: 2, surrender: 1 },
    },
  },
};

// ── EASTER EGGS (item-on-hotspot, but for joy) ───────────────
export const EASTER_EGGS = {
  "rubber_ball>laundry_hoop": {
    once: "egg_nothin_but_net",
    result: {
      ok: true,
      message:
        "You toe the chalk line. Breathe. Shoot.\n\nSWISH.\n\nSomewhere in the walls, something ENORMOUS gasps — then loses its mind. The veins pulse like a stadium wave. A mushroom does a little dance. The house... celebrates with you.",
      setFlags: { egg_nothin_but_net: true },
      trophy: { id: "nothin_but_net", name: "Nothin' But Net", desc: "Even the Tenant respects the arc." },
    },
  },

  "rubber_ball>tub": {
    once: "egg_bath_time",
    result: {
      ok: true,
      message:
        "You set the ball on the black water. It floats. Of course it floats. It bobs there, cheerful and red, the single most innocent object this house has ever contained.\n\nFrom deep in the pipes: a giggle. You both pretend that didn't happen. You take the ball back.",
      setFlags: { egg_bath_time: true },
      trophy: { id: "bath_time", name: "Bath Time", desc: "Rub-a-dub-dub, one ball in a cursed tub." },
    },
  },

  "rubber_ball>mouth_door": {
    once: "egg_fetch",
    result: {
      ok: true,
      message:
        "You bounce the ball once, twice — and hurl it at the face.\n\nThe mouth SNAPS it out of the air. Chews. Considers. The whole hall holds its breath...\n\n...and it spits the ball back, gently, right into your hands. Cleaner than before. The mouth is, unmistakably, wagging.",
      setFlags: { egg_fetch: true },
      trophy: { id: "fetch", name: "Fetch", desc: "Who's a good door. WHO'S A GOOD DOOR." },
    },
  },
  "rubber_ball>mouth_door_open": {
    once: "egg_fetch",
    result: {
      ok: true,
      message:
        "You bounce the ball once, twice — and hurl it at the open mouth.\n\nSNAP. Chew. Consider...\n\n...and it spits the ball back, gently, right into your hands. Cleaner than before. The mouth is, unmistakably, wagging.",
      setFlags: { egg_fetch: true },
      trophy: { id: "fetch", name: "Fetch", desc: "Who's a good door. WHO'S A GOOD DOOR." },
    },
  },

  "torn_page>crib": {
    once: "egg_one_more_story",
    result: {
      ok: true,
      message:
        "You don't know why you do it. You hold the torn page up to the crib, and you read it aloud — the moons, dark to light, the way a bedtime story ends.\n\nThe blanket-shape settles. Really settles, the way a kid finally goes still.\n\nThe room is warmer when you leave it.",
      setFlags: { egg_one_more_story: true },
      trophy: { id: "one_more_story", name: "One More Story", desc: "You read to the empty crib. It wasn't empty. That's okay." },
    },
  },

  "tarnished_locket>portrait_boys": {
    once: "egg_what_was_saved",
    result: {
      ok: true,
      message:
        "You hold the locket up beside the painted one. For a second they're the same object.\n\nThe fused seam parts with a sigh fifty years long. Inside: a sliver of painted portrait — two boys, mid-laugh — and a curl of hair, tied with thread.\n\nYou close it. It closes easily now. You keep it. Obviously you keep it.",
      setFlags: { egg_what_was_saved: true },
      trophy: { id: "what_was_saved", name: "What Was Saved", desc: "for my little brother." },
    },
  },
};

// ── BOOKSHELF SEQUENCE (Study) ───────────────────────────────
// Clue: torn page — "shelve the moons from dark to light"
// Correct press order: new(0) → crescent(1) → half(2) → full(3)
export function pressBook(bookOrder, state) {
  if (state.flags.bookshelf_solved) {
    return { ok: false, message: "The compartment is already open." };
  }
  const seq = state.flags.book_seq || [];
  const expected = seq.length;
  if (bookOrder === expected) {
    const next = [...seq, bookOrder];
    if (next.length === 4) {
      return {
        ok: true,
        message:
          "The fourth spine sinks with a CLACK. Behind the books, a hidden compartment swings open — a crescent-shaped winding key rests inside. You take it.",
        grantItems: ["crescent_key"],
        setFlags: { book_seq: [], bookshelf_solved: true },
        majorTask: true,
      };
    }
    return {
      ok: true,
      message: `The spine sinks in and holds. (${next.length}/4)`,
      setFlags: { book_seq: next },
    };
  }
  return {
    ok: false,
    message: "The spines all spring back out with a disapproving THUNK. Dark to light, the page said. Start over.",
    setFlags: { book_seq: [] },
  };
}

// ── DRAIN CHAIN (Bathroom) ───────────────────────────────────
export function pressDrain(state) {
  if (state.flags.tub_drained) {
    return { ok: false, message: "The tub is already empty. The chain hangs slack, pleased with itself." };
  }
  return {
    ok: true,
    message:
      "You haul the chain. Somewhere under the tub, a stopper the size of a manhole gives way —\n\n— and the black water doesn't drain so much as get SWALLOWED, spiraling down in seconds with a sound like the house gargling.\n\nThe tub is empty. Things are visible in the drain.",
    setFlags: { tub_drained: true },
    majorTask: true,
  };
}

// ── GRANDFATHER CLOCK (Master Bedroom) ───────────────────────
// Clue: bathroom mirror — seven o'clock. Chime it seven times.
export function pressClock(state) {
  if (state.flags.clock_solved) {
    return { ok: false, message: "The clock has said its piece." };
  }
  const n = (state.flags.clock_chimes || 0) + 1;
  if (n === 7) {
    return {
      ok: true,
      message:
        "The SEVENTH chime doesn't ring — it BREAKS, like a voice does. The pendulum stops itself. The case swings open on its own...\n\n...and behind the works, hung from a nail in a velvet pouch: a BLACK IRON KEY, colder than the room.\n\nThe clock, having finally grieved, begins — softly — to tick.",
      grantItems: ["black_key"],
      setFlags: { clock_solved: true, clock_chimes: 0 },
      majorTask: true,
    };
  }
  const flavor = [
    "",
    "You press the pendulum. One chime, deep as a well. (1)",
    "A second chime. The dust on the case stirs. (2)",
    "Three. Somewhere in the house, a door politely closes. (3)",
    "Four. The chime hangs in the air longer than it should. (4)",
    "Five. You'd swear the clock leaned toward you. (5)",
    "Six. The whole room is listening now. (6)",
  ][n] || `The clock chimes. (${n})`;
  return {
    ok: true,
    message: flavor,
    setFlags: { clock_chimes: n },
  };
}

// ── PRESS DISPATCH — one entry point for all press puzzles ───
export function pressHotspot(hotspot, state) {
  if (hotspot.bookOrder !== undefined) return pressBook(hotspot.bookOrder, state);
  if (hotspot.puzzle === "drain") return pressDrain(state);
  if (hotspot.puzzle === "clock") return pressClock(state);
  return { ok: false, message: "Nothing happens. Yet." };
}

// ── DOORS ─────────────────────────────────────────────────────
export function tryDoor(doorId, state) {
  const door = DOORS.find((d) => d.id === doorId);
  if (!door) return { ok: false, message: "..." };

  if (state.unlockedDoors.includes(doorId)) {
    return { ok: true, enterRoom: door.room };
  }

  const lastTooth = state.unlockedDoors.length + 1 >= DOORS.length;
  const allOpenTag =
    "\n\nBehind you, all down the hall, the house INHALES. The Mouth Door begins — audibly — to drool.\n\nSix teeth. All open. Go talk to it.";

  if (door.lockedBy === null) {
    return {
      ok: true,
      enterRoom: door.room,
      unlockDoor: doorId,
      setFlags: lastTooth ? { all_doors_open: true } : undefined,
      message: lastTooth ? "The door swings open." + allOpenTag : null,
      majorTask: lastTooth,
    };
  }

  if (state.inventory.includes(door.lockedBy)) {
    const base =
      door.unlockMsg ||
      `The ${ITEMS[door.lockedBy].name} turns. The door remembers how to open.`;
    return {
      ok: true,
      enterRoom: door.room,
      unlockDoor: doorId,
      consumeItems: [door.lockedBy],
      setFlags: lastTooth ? { all_doors_open: true } : undefined,
      message: lastTooth ? base + allOpenTag : base,
      majorTask: true,
    };
  }

  return {
    ok: false,
    message: door.lockHint || "Locked tight. Something specific fits this one.",
  };
}

// ── IN-WORLD CHOICES ──────────────────────────────────────────
// Some hotspots present a CHOICE when inspected (roomData: `roomChoices`).
// Each choice id maps to a result here, exactly like a USE_RULE result,
// so App.js applies it through the same applyResult path. This is how
// the house collects "drives" without requiring an item in hand — the
// foundation for the many-endings tree. Each choice fires once (its
// `once` flag), then the hotspot stops offering it.
//
// choice shape in roomData: { id, label, hint? }
// outcome shape here:       { once?, result:{...}, repeatMessage? }
export const CHOICE_OUTCOMES = {
  // ── The Crib (Nursery): rock it, or walk away ──
  crib_rock: {
    once: "chose_crib_rock",
    result: {
      ok: true,
      message:
        "You rock the empty crib, slowly, the way you'd rock a real one. The blanket-shape settles into the motion and goes trustingly still. You catch yourself humming. You make yourself stop. You keep rocking.",
      setFlags: { chose_crib_rock: true },
      addDrive: { mercy: 2 },
    },
  },
  crib_tip: {
    once: "chose_crib_tip",
    result: {
      ok: true,
      message:
        "You grab the iron rail and TIP the whole crib over. The blanket-shape spills out — nothing, of course, just air and old cotton — and lands wrong, the way a body lands. The temperature in the room drops a degree for every second you don't apologize. You don't apologize.",
      setFlags: { chose_crib_tip: true },
      addDrive: { cruelty: 3 },
      trophy: { id: "rock_a_bye", name: "Rock-a-Bye", desc: "There was nothing in the crib. You made sure it knew you'd checked, cruelly." },
    },
  },

  // ── The Portrait of the Boys (Master): bow your head, or tear it down ──
  portrait_mourn: {
    once: "chose_portrait_mourn",
    result: {
      ok: true,
      message:
        "You stand in front of the two laughing boys and you let yourself feel it — the whole shape of the thing, the brother-sized hole. You touch the frame once, gently. The painted locket seems to catch a light there is no source for.",
      setFlags: { chose_portrait_mourn: true },
      addDrive: { mercy: 2, surrender: 1 },
    },
  },
  portrait_tear: {
    once: "smashed_portrait",
    result: {
      ok: true,
      message:
        "You take the portrait off the wall and put your fist through it — through the laughing faces, through the little painted locket, through whatever sentiment the house was trying to sell.\n\nFor one second, in the torn canvas, you'd SWEAR both painted boys turn to look at you. Then it's just a ruined picture, and the hall has gone very, very cold.",
      setFlags: { smashed_portrait: true },
      addDrive: { cruelty: 3 },
      trophy: { id: "no_sentiment", name: "No Sentiment", desc: "The house offered you a feeling. You put your fist through it." },
    },
  },

  // ── The Builder's Door (Master wardrobe, after found_the_way_out): leave, or stay ──
  builders_door_open: {
    once: "chose_to_leave",
    result: {
      ok: true,
      message:
        "Your hand is on the latch of the small plain door. Cold honest night air leaks around its edge. You could just... go. Right now. Leave the stairs, the toll, the voice below, all of it, behind you in the dark.\n\nYou decide that you will. When the moment comes at the Mouth Door — you walk.",
      setFlags: { chose_to_leave: true },
      addDrive: { defiance: 2 },
    },
  },
  builders_door_seal: {
    once: "chose_to_stay",
    result: {
      ok: true,
      message:
        "You look at the way out for a long time. Then you push the wardrobe back over it. Whatever's waiting at the bottom of the stairs — your brother's voice, the truth, the worst thing — you didn't come this far to leave before you've seen it.\n\nThe house, somewhere, relaxes. It does so love a guest who stays.",
      setFlags: { chose_to_stay: true },
      addDrive: { surrender: 2, curiosity: 1 },
    },
  },

  // ── The Root of the Veins (Cellar): listen closer, or recoil ──
  veins_listen: {
    once: "chose_veins_listen",
    result: {
      ok: true,
      message:
        "You press your whole ear flat to the bundle of roots and you LISTEN, past the heartbeat, past the humming, down to the place the sounds come from. You shouldn't understand it. You do. It is asking, very politely, to be let all the way in.\n\nYou pull back, but slower than you should have. You heard it. It knows you heard it.",
      setFlags: { chose_veins_listen: true },
      addDrive: { curiosity: 3 },
    },
  },

  // ── The Old Foundation (Cellar): kneel and apologize, or spit on it ──
  foundation_kneel: {
    once: "chose_foundation_kneel",
    result: {
      ok: true,
      message:
        "You kneel on the body-warm stones of the house that tried to kill you when you were small, and you say you're sorry — to the first family, to the brother who didn't get out, to the ground itself for building on its grief. The veins around you go still and, you'd swear, grateful.",
      setFlags: { chose_foundation_kneel: true },
      addDrive: { mercy: 1, surrender: 2 },
    },
  },
  foundation_spit: {
    once: "chose_foundation_spit",
    result: {
      ok: true,
      message:
        "You spit on the old foundation. 'You're a HOUSE,' you tell it. 'You're warm rocks and a sob story and I am not afraid of real estate.'\n\nThe heartbeat in the roots skips. Then doubles. You have, perhaps, said the wrong thing to the thing that is holding something down.",
      setFlags: { chose_foundation_spit: true },
      addDrive: { cruelty: 2, defiance: 2 },
    },
  },

  // ── THE LANDLADY'S OFFICE (Study hidden chamber) ──
  portrait_look_behind: {
    once: "found_landladys_office",
    result: {
      ok: true,
      message:
        "You swing the heavy frame aside on a hinge you didn't know was there. Behind it: a cramped office, a board of a hundred named keys, and a ledger left open mid-entry. The candle on the desk lights itself as you lean in.",
      setFlags: { found_landladys_office: true },
      addDrive: { curiosity: 3 },
      scare: true,
      trophy: { id: "behind_the_frame", name: "Behind the Frame", desc: "Every house has a room it doesn't show the buyer. You found this one's." },
    },
  },
  office_read_ledger: {
    once: "read_the_ledger",
    result: {
      ok: true,
      message:
        "You read the ledger to the very bottom. Every tenant. Every fire. Every name on every key, and beside each, in the same patient hand, a single word: FED, or FLED, or STAYED.\n\nThe second-to-last entry is a family you don't recognize — and the small note beside the youngest name stops your heart, because it isn't about them. It's about YOU. It's been waiting for you to read it. You understand, now, exactly whose grief you've been wearing.",
      setFlags: { read_the_ledger: true },
      addDrive: { curiosity: 4 },
    },
  },
  office_burn_ledger: {
    once: "burned_the_ledger",
    result: {
      ok: true,
      message:
        "You hold the ledger to the self-lit candle. It does not want to burn — the pages are damp with something that isn't water — but you make them. A hundred names curl into smoke, and somewhere far below, something that had been counting on being remembered makes a sound you will hear in your sleep for the rest of your life.\n\nSome mercies are also cruelties. This was both.",
      setFlags: { burned_the_ledger: true },
      addDrive: { mercy: 2, cruelty: 2 },
      scare: true,
      trophy: { id: "unwritten", name: "Unwritten", desc: "You burned the record of every soul the house ever kept. Including the entry with your name." },
    },
  },

  // ── THE MOUTH DOOR confrontations — NON-TOLL ending triggers ──
  mouth_refuse_leave: {
    result: {
      ok: true,
      message:
        "You step back from the drooling mouth. 'No,' you tell it. 'I found another way. I'm not paying anything.'",
      addDrive: { defiance: 2 },
      startScript: "finaleNoToll",
    },
  },
  mouth_walk_away: {
    result: {
      ok: true,
      message:
        "You look the whole impossible face dead in its grain and you say it flat: 'No.' No toll. No stairs. No lesson. You turn your back on it — the rudest thing anyone has ever done to it — and you walk.",
      addDrive: { defiance: 3, cruelty: 1 },
      startScript: "finaleNoToll",
    },
  },
};

// Resolve an in-world choice. Mirrors resolveUse's once/repeat handling.
export function resolveChoice(choiceId, state) {
  const c = CHOICE_OUTCOMES[choiceId];
  if (!c) return { ok: false, message: "Nothing comes of it." };
  if (c.once && state.flags[c.once]) {
    return { ok: false, message: c.repeatMessage || "You've already made that choice. The house remembers it." };
  }
  return c.result;
}

// ── USE RESOLUTION ────────────────────────────────────────────
export function resolveUse(itemId, hotspotId, state) {
  const key = `${itemId}>${hotspotId}`;

  const egg = EASTER_EGGS[key];
  if (egg) {
    if (egg.once && state.flags[egg.once]) {
      return { ok: false, message: "The house enjoyed that the first time. It's still talking about it." };
    }
    return egg.result;
  }

  const rule = USE_RULES[key];
  if (rule) {
    if (rule.once && state.flags[rule.once]) {
      return { ok: false, message: rule.repeatMessage || "That's already done." };
    }
    const missing = (rule.requiresFlags || []).filter((f) => !state.flags[f]);
    if (missing.length) {
      return { ok: false, message: rule.failMessage || "Not yet. Something else has to happen first." };
    }
    return rule.result;
  }

  return {
    ok: false,
    message: `You hold the ${ITEMS[itemId]?.name || "item"} up to it. The house is unmoved. Maybe somewhere else.`,
  };
}

// ── EASY-MODE HINT: which carried items currently SOLVE a hotspot ──
// Returns the set of inventory item ids that, used on `hotspotId` right
// now, would produce a real progression result (a USE_RULE that is not
// already spent and whose prerequisite flags are met). Easter eggs are
// deliberately excluded — they're flavor, not the "appropriate item."
// Easy Mode highlights these and dims the rest; if the set is empty,
// every pocket item is dimmed. Hard Mode never calls this.
export function solutionItemsFor(hotspotId, state) {
  const out = [];
  for (const itemId of state.inventory || []) {
    const rule = USE_RULES[`${itemId}>${hotspotId}`];
    if (!rule) continue;
    if (rule.once && state.flags[rule.once]) continue;
    const missing = (rule.requiresFlags || []).filter((f) => !state.flags[f]);
    if (missing.length) continue;
    out.push(itemId);
  }
  return out;
}
